import * as loadjs from 'loadjs'
import { getQueryParam } from '../helpers/getQueryParam'
import IVideoConfig from '../../typings/IVideoConfig'
import IVideoOptions from '../../typings/IVideoOptions'
import EventManager from '../eventManager/EventManager'
import StickyVideo, { MEDIAVINE_VIDEO_URL } from './StickyVideo'
import mvFetch from '../helpers/mvFetch'

export async function setupPlacement(
  config: IVideoConfig,
  targetDiv: HTMLDivElement
): Promise<void> {
  // We save playerGroup on global window.MediavineVideo.playerGroup for two reasons.
  // 1.) We want to make sure that only one playerGroup is used for every video on a single page view.
  // 2.) We need to expose the playerGroup for WebWrapper to send it to GAM.

  if (!window.MediavineVideo) {
    // @ts-ignore
    window.MediavineVideo = {}
  }
  // @ts-ignore
  window.MediavineVideo.playerGroup =
    // @ts-ignore
    window.MediavineVideo.playerGroup || config.playerGroup

  const optsFromDiv = getDivOptions(targetDiv)
  const debugSrc = getQueryParam('debug_src') as string
  const playerSource =
    debugSrc || `${config.playerSource}/${config.playerVersion}`

  config.playerSource = debugSrc || playerSource

  EventManager.trigger(EventManager.events.initLauncher, config)

  if (!optsFromDiv) {
    console.error('video options missing:', targetDiv)
    return
  }

  if (!targetDiv) {
    console.error('video target div is missing.')
  }
  config.targetDiv = targetDiv

  // Avoid loading placement.js multiple times
  if (window?.MediavineVideo?.placement?.render) {
    window.MediavineVideo.placement.render(config, optsFromDiv)
  } else {
    loadjs(`${playerSource}/placement.js`, () => {
      if (window.MediavineVideo) {
        window.MediavineVideo.placement.render(config, optsFromDiv)
      }
    })
  }
}

export function getDivOptions(
  optionsDiv: HTMLDivElement
): Partial<IVideoOptions> {
  if (!optionsDiv) {
    return {}
  }

  const callbacksValue = optionsDiv.getAttribute('data-callbacks')
  const callbacks = callbacksValue ? window[callbacksValue] : {}

  return {
    aspectRatio: optionsDiv.getAttribute('data-ratio') || undefined,
    autoInsert: isTruthyAttribute(optionsDiv.getAttribute('data-autoInsert')),
    disableJsonLD: isTruthyAttribute(
      optionsDiv.getAttribute('data-disable-jsonld')
    ),
    height: Number(optionsDiv.getAttribute('data-height')) || 0,
    isAutomobile: isTruthyAttribute(optionsDiv.getAttribute('data-automobile')),
    isAutoplay: isTruthyAttribute(optionsDiv.getAttribute('data-autoplay')),
    isMuted: isTruthyAttribute(optionsDiv.getAttribute('data-muted')),
    sticky: isTruthyAttribute(optionsDiv.getAttribute('data-sticky')),
    title: optionsDiv.getAttribute('data-title') || undefined,
    volume: Number(optionsDiv.getAttribute('data-volume')) || 0,
    width: Number(optionsDiv.getAttribute('data-width')) || 0,

    permalink: optionsDiv.getAttribute('data-permalink') || undefined,
    srcPoster: optionsDiv.getAttribute('data-srcPoster') || undefined,
    srcHls: optionsDiv.getAttribute('data-srcHls') || undefined,
    srcHlsH265: optionsDiv.getAttribute('data-srcHlsH265') || undefined,
    srcDash: optionsDiv.getAttribute('data-srcDash') || undefined,

    onAdStarted: callbacks.onAdStarted,
    onEnded: callbacks.onEnded,
    onError: callbacks.onError,
    onViewable: callbacks.onViewable
  }
}

// LD+JSON needs to be rendered on page load to ensure it is indexed by Google.
export async function loadDataAndWriteLdJsonForTargetDiv(
  targetDiv: HTMLDivElement
) {
  const videoId = StickyVideo.getVideoIdFromTargetDiv(targetDiv)
  const playlistId = StickyVideo.getPlaylistIdFromTargetDiv(targetDiv)
  const configs = StickyVideo.videoConfigs
  const slug = window.$mediavine.web.model.slug
  const optsFromDiv = getDivOptions(targetDiv)

  // If both exist (they shouldn't), we'll go with the video over the playlist.
  if (videoId) {
    if (!configs[videoId]) {
      configs[videoId] = await mvFetch.fetchJson(
        `https://${MEDIAVINE_VIDEO_URL}/videos/${videoId}.json`,
        'GET'
      )
      if (!optsFromDiv.disableJsonLD) {
        createLDJson(configs[videoId])
      }
    }
  } else if (playlistId) {
    if (!configs[playlistId]) {
      configs[playlistId] = await mvFetch.fetchJson(
        `https://${MEDIAVINE_VIDEO_URL}/playlists/${slug}/${playlistId}.json`,
        'GET'
      )
      if (!optsFromDiv.disableJsonLD) {
        createLDJson(configs[playlistId])
      }
    }
  }
}

export async function setupPlacementForTargetDiv(targetDiv: HTMLDivElement) {
  const videoId = StickyVideo.getVideoIdFromTargetDiv(targetDiv)
  const playlistId = StickyVideo.getPlaylistIdFromTargetDiv(targetDiv)
  const configs = StickyVideo.videoConfigs

  // loadDataAndWriteLdJsonForTargetDiv won't do anything if it has
  // already been called for this targetDiv.
  await loadDataAndWriteLdJsonForTargetDiv(targetDiv)

  // If both exist (they shouldn't), we'll go with the video over the playlist.
  if (videoId) {
    setupPlacement({ ...configs[videoId] }, targetDiv)
  } else if (playlistId) {
    setupPlacement({ ...configs[playlistId] }, targetDiv)
  }
}

export function createLDJson(config: IVideoConfig): void {
  const script = document.createElement('script')
  const head = document.head || document.getElementsByTagName('head')[0]
  const meta = JSON.stringify(config.video.meta)

  script.className = 'mediavine-video__ld-json'
  script.setAttribute('type', 'application/ld+json')

  try {
    script.appendChild(document.createTextNode(meta))
    head.appendChild(script)
  } catch (e) {
    script.text = meta
    head.appendChild(script)
  }
}

function isTruthyAttribute(value: any): boolean {
  return ['1', 'true', true, 1].indexOf(value) !== -1
}
